// React

// Components
import { gql, useMutation, useQuery, useSubscription } from "@apollo/client";
import { Card, Collapse, Grid, Icon, Tooltip } from "@mui/material";

// Data

import CartelError from "components/Carteles/Error";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
// import SoftInput from "components/SoftInput";
import SoftBadge from "components/SoftBadge";
import { MenuContext } from "context/menuContext";
import dayjs from "dayjs";
import { getUproColor } from "utils/colors";
import { formatDate } from "utils/formatters";
import ModalPedido from "../areas/components/ModalPedido";
import ModalMesa from "./ModalMesa";
import { UserContext } from "context/user";

const NUEVO_PEDIDO_SUBSCRIPTION = gql`
  subscription {
    nuevoPedido { id }
  }
`;

const PEDIDO_MODIFICADO_SUBSCRIPTION = gql`
  subscription {
    pedidoModificado { id }
  }
`;

const PEDIDO_ELIMINADO_SUBSCRIPTION = gql`
  subscription {
    pedidoEliminado { id }
  }
`;

const compareMesasByOrden = (mesaA, mesaB) => {
  const ordenA = Number.isFinite(Number(mesaA?.orden)) ? Number(mesaA.orden) : Number.MAX_SAFE_INTEGER;
  const ordenB = Number.isFinite(Number(mesaB?.orden)) ? Number(mesaB.orden) : Number.MAX_SAFE_INTEGER;

  if (ordenA !== ordenB) return ordenA - ordenB;

  const numeroA = `${mesaA?.numero || ""}`;
  const numeroB = `${mesaB?.numero || ""}`;
  const byNumero = numeroA.localeCompare(numeroB, "es", {
    numeric: true,
    sensitivity: "base",
  });

  if (byNumero !== 0) return byNumero;

  return Number(mesaA?.id || 0) - Number(mesaB?.id || 0);
};

const sortMesas = (mesas = []) => [...mesas].sort(compareMesasByOrden);

const applyMesaOrder = (mesas = []) =>
  mesas.map((mesa, index) => ({
    ...mesa,
    orden: index + 1,
  }));

const haveSameMesaOrder = (mesasA = [], mesasB = []) =>
  mesasA.length === mesasB.length &&
  mesasA.every((mesa, index) => Number(mesa?.id) === Number(mesasB[index]?.id));

const reorderMesas = (mesas = [], mesaId, insertIndex) => {
  const sourceIndex = mesas.findIndex((mesa) => Number(mesa.id) === Number(mesaId));
  if (sourceIndex === -1) return mesas;

  const nextMesas = [...mesas];
  const [draggedMesa] = nextMesas.splice(sourceIndex, 1);
  const safeIndex = Math.max(0, Math.min(insertIndex, nextMesas.length));
  nextMesas.splice(safeIndex, 0, draggedMesa);
  return applyMesaOrder(nextMesas);
};

function Mesas() {
  const [modalPedido, setModalPedido] = useState(false);
  const [mesaElegida, setMesaElegida] = useState(null);
  const [stockRefreshTick, setStockRefreshTick] = useState(0);
  const [selectedMesa, setSelectedMesa] = useState(null);
  const [mesas, setMesas] = useState([]);
  const [modoReordenar, setModoReordenar] = useState(false);
  const [draggingMesaId, setDraggingMesaId] = useState(null);
  const [dragOverlay, setDragOverlay] = useState(null);
  const refreshTimeoutRef = useRef(null);
  const dragStartMesasRef = useRef([]);
  const mesasRef = useRef([]);
  const itemRefs = useRef(new Map());
  const dragListenersCleanupRef = useRef(null);
  const bodyStyleRef = useRef(null);
  const { data: newPedidoEvent } = useSubscription(NUEVO_PEDIDO_SUBSCRIPTION);
  const { data: modifiedPedidoEvent } = useSubscription(PEDIDO_MODIFICADO_SUBSCRIPTION);
  const { data: deletedPedidoEvent } = useSubscription(PEDIDO_ELIMINADO_SUBSCRIPTION);
  const [modalMesa, setModalMesa] = useState(false);

  const { user } = useContext(UserContext);
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const { menu } = useContext(MenuContext);

  const esMozo = user?.tipoUser == "Mozo";

  const { loading, error, data, refetch } = useQuery(
    gql`
      query getMesas($filter: JSON, $order: Order) {
        mesas(filter: $filter, order: $order) {
          id
          numero
          orden
          posicionX
          posicionY
          idArea
          estado
          pedido {
            id
            paraLlevar
            comentario
            createdAt
            mozo {
              id
              nombre
              apellido
            }
            detalles {
              id
              cantidad
              estado
              numeroComanda
              precio
              horarioEntrega
              comentario
              motivoCancelacion
              impresionComanda
              producto {
                id
                nombre
                seccion
                comanderas {
                  id
                  printName
                  habilitado
                  estadoConexion
                }
                combo
                esElaborado
                precioCarta
                precioOnline
                precioPorMayor
                precioPorMayorOnline
                cantidadPorMayor
              }
              ingredientesQuitados {
                id
              }
            }
          }
          area {
            id
            nombre
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
      variables: {
        order: { field: "orden", order: "ASC" },
      },
    }
  );

  const [deleteMesa, { loading: loadingDeleteMesa }] = useMutation(
    gql`
      mutation deleteMesa($id: ID!) {
        deleteMesa(id: $id) {
          id
        }
      }
    `,
    {
      fetchPolicy: "network-only",
    }
  );

  const [saveMesasOrden, { loading: loadingSaveMesasOrden }] = useMutation(
    gql`
      mutation saveMesasOrden($input: [MesaInput]!) {
        saveMesas(input: $input) {
          id
          orden
        }
      }
    `
  );

  const [savePedido, { loading: loadingSave }] = useMutation(
    gql`
      mutation savePedido($input: PedidoInput!) {
        savePedido(input: $input) {
          id
          paraLlevar
          comentario
          mesa {
            numero
          }
          detalles {
            id
            producto {
              nombre
              seccion
              comanderas {
                id
                printName
                habilitado
                estadoConexion
              }
            }
            comentario
            cantidad
            numeroComanda
            esElaborado
            impresionComanda
          }
          updatedAt
        }
      }
    `
  );

  useEffect(() => {
    const nextMesas = sortMesas(data?.mesas || []);
    setMesas(nextMesas);
    mesasRef.current = nextMesas;
  }, [data?.mesas]);

  useEffect(() => {
    mesasRef.current = mesas;
  }, [mesas]);

  useEffect(() => {
    if (!newPedidoEvent && !modifiedPedidoEvent && !deletedPedidoEvent) return;

    if (refreshTimeoutRef.current) return;

    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      refetch();
      setStockRefreshTick((prev) => prev + 1);
    }, 180);
  }, [deletedPedidoEvent, modifiedPedidoEvent, newPedidoEvent, refetch]);

  useEffect(
    () => () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (dragListenersCleanupRef.current) {
        dragListenersCleanupRef.current();
      }
      if (bodyStyleRef.current) {
        document.body.style.userSelect = bodyStyleRef.current.userSelect;
        document.body.style.webkitUserSelect = bodyStyleRef.current.webkitUserSelect;
        document.body.style.touchAction = bodyStyleRef.current.touchAction;
      }
    },
    []
  );

  const persistMesaOrder = async (nextMesas, previousMesas) => {
    setMesas(nextMesas);

    try {
      await saveMesasOrden({
        variables: {
          input: nextMesas.map((mesa, index) => ({
            id: mesa.id,
            orden: index + 1,
          })),
        },
      });
      handleSnackbar("Orden de mesas actualizado", "success");
      refetch();
    } catch (errorSave) {
      setMesas(previousMesas);
      handleSnackbar(errorSave?.graphQLErrors?.[0]?.message || "Error al guardar el orden", "error");
    }
  };

  const resetDragState = () => {
    setDraggingMesaId(null);
    setDragOverlay(null);
    dragStartMesasRef.current = [];

    if (dragListenersCleanupRef.current) {
      dragListenersCleanupRef.current();
      dragListenersCleanupRef.current = null;
    }

    if (bodyStyleRef.current) {
      document.body.style.userSelect = bodyStyleRef.current.userSelect;
      document.body.style.webkitUserSelect = bodyStyleRef.current.webkitUserSelect;
      document.body.style.touchAction = bodyStyleRef.current.touchAction;
      bodyStyleRef.current = null;
    }
  };

  const getInsertIndexFromPointer = (mesaId, draggedCenterY) => {
    const mesasSinArrastre = mesasRef.current.filter((mesa) => Number(mesa.id) !== Number(mesaId));

    let insertIndex = mesasSinArrastre.length;

    for (let index = 0; index < mesasSinArrastre.length; index += 1) {
      const mesa = mesasSinArrastre[index];
      const element = itemRefs.current.get(mesa.id);
      if (!element) continue;

      const rect = element.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;

      if (draggedCenterY < midpoint) {
        insertIndex = index;
        break;
      }
    }

    return insertIndex;
  };

  const startDrag = (event, mesaId) => {
    if (!modoReordenar || loadingSaveMesasOrden) return;

    const pointerY = event.clientY ?? event.touches?.[0]?.clientY;
    if (typeof pointerY !== "number") return;

    const itemNode = itemRefs.current.get(mesaId);
    if (!itemNode) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = itemNode.getBoundingClientRect();
    const pointerOffsetY = pointerY - rect.top;

    dragStartMesasRef.current = mesasRef.current;
    setDraggingMesaId(mesaId);
    setDragOverlay({
      mesaId,
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      pointerOffsetY,
    });

    bodyStyleRef.current = {
      userSelect: document.body.style.userSelect,
      webkitUserSelect: document.body.style.webkitUserSelect,
      touchAction: document.body.style.touchAction,
    };
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";
    document.body.style.touchAction = "none";

    const handlePointerMove = (moveEvent) => {
      const movePointerY = moveEvent.clientY;
      if (typeof movePointerY !== "number") return;

      moveEvent.preventDefault();

      const nextTop = movePointerY - pointerOffsetY;
      const draggedCenterY = nextTop + rect.height / 2;
      const insertIndex = getInsertIndexFromPointer(mesaId, draggedCenterY);
      const nextMesas = reorderMesas(mesasRef.current, mesaId, insertIndex);

      setDragOverlay((prev) =>
        prev
          ? {
            ...prev,
            top: nextTop,
          }
          : prev
      );

      if (!haveSameMesaOrder(nextMesas, mesasRef.current)) {
        setMesas(nextMesas);
      }
    };

    const handlePointerUp = async (upEvent) => {
      upEvent.preventDefault();

      const previousMesas = dragStartMesasRef.current?.length ? dragStartMesasRef.current : mesasRef.current;
      const finalMesas = mesasRef.current;

      resetDragState();

      if (haveSameMesaOrder(finalMesas, previousMesas)) {
        setMesas(previousMesas);
        return;
      }

      await persistMesaOrder(finalMesas, previousMesas);
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: false });
    window.addEventListener("pointerup", handlePointerUp, { passive: false });
    window.addEventListener("pointercancel", handlePointerUp, { passive: false });

    dragListenersCleanupRef.current = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  };

  const ejecutarAccionUsuario = (accion, item) => {
    switch (accion) {
      case "editar":
        setModalMesa(true);
        setSelectedMesa(item);
        break;
      case "borrar":
        if (!loadingDeleteMesa) {
          confirmar({
            title: "Advertencia",
            message: "Esta seguro que desea borrar? Esta accion no se puede deshacer.",
            icon: "warning",
            func: () => {
              deleteMesa({ variables: { id: item.id } })
                .then(() => {
                  handleSnackbar("Borrado correctamente", "success");
                  refetch();
                })
                .catch(() => {
                  handleSnackbar("Ha ocurrido un error al borrar", "error");
                });
            },
          });
        }
        break;
      case "elegirMesa":
        if (modoReordenar) return;
        setMesaElegida(item);
        setModalPedido(true);
        break;
      default:
        break;
    }
  };

  const mesaArrastrada = useMemo(
    () =>
      draggingMesaId
        ? mesas.find((mesa) => Number(mesa.id) === Number(draggingMesaId)) ||
        dragStartMesasRef.current.find((mesa) => Number(mesa.id) === Number(draggingMesaId))
        : null,
    [draggingMesaId, mesas]
  );

  const renderMesaCard = (item, dragging = false) => (
    <Card
      sx={{
        border: dragging ? `2px solid ${getUproColor("uproBlue")}` : "2px solid transparent",
        boxShadow: dragging ? "0 16px 32px rgba(0,0,0,0.16)" : undefined,
        transform: dragging ? "scale(1.01)" : "none",
        userSelect: modoReordenar ? "none" : "auto",
        WebkitUserSelect: modoReordenar ? "none" : "auto",
        WebkitTouchCallout: modoReordenar ? "none" : "default",
      }}
    >
      <SoftBox p={2} display="flex" justifyContent="space-between" alignItems="center">
        <SoftBox display="flex" flexDirection="column" gap={1}>
          <SoftBox display="flex" gap={1} alignItems="center">
            <SoftTypography variant="h6" fontWeight="bold">
              Mesa {item.numero}
            </SoftTypography>
            <SoftBadge
              badgeContent={item.estado}
              color={
                item?.estado === "ESPERANDO"
                  ? "uproYellow"
                  : item?.estado === "OCUPADA"
                    ? "primary"
                    : "uproGreen"
              }
            />
          </SoftBox>
          <SoftBox display="flex" gap={1} flexWrap="wrap">
            <SoftBox display="flex" gap={1} alignItems="center">
              <Icon>person</Icon>
              <SoftTypography variant="h7" fontWeight="regular">
                {item?.pedido?.mozo
                  ? `${item?.pedido?.mozo?.nombre} ${item?.pedido?.mozo?.apellido}`
                  : "Sin mozo asignado"}
              </SoftTypography>
            </SoftBox>
            <Collapse in={item?.pedido?.createdAt}>
              <SoftBox display="flex" gap={1} alignItems="center">
                <Icon>schedule</Icon>
                <SoftTypography variant="h7" fontWeight="regular">
                  {dayjs(item?.pedido?.createdAt).isAfter(dayjs().subtract(2, "day"))
                    ? formatDate(item?.pedido?.createdAt, "XXXX a las hh:mm")
                    : formatDate(item?.pedido?.createdAt, "dd/MM/yyyy a las hh:mm")}
                </SoftTypography>
              </SoftBox>
            </Collapse>
          </SoftBox>
        </SoftBox>
        {modoReordenar ? (
          <Tooltip title="Mantenga presionado y arrastre" placement="top">
            <SoftBox
              onPointerDown={(event) => startDrag(event, item.id)}
              onClick={(event) => {
                event.stopPropagation();
              }}
              sx={{
                zIndex: 100,
                minWidth: 48,
                minHeight: 48,
                width: 48,
                height: 48,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: `${getUproColor("uproBlue")}22`,
                color: getUproColor("uproBlue"),
                border: `1px solid ${getUproColor("uproBlue")}55`,
                cursor: loadingSaveMesasOrden ? "wait" : "grab",
                touchAction: "none",
                userSelect: "none",
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",
              }}
            >
              <Icon>drag_indicator</Icon>
            </SoftBox>
          </Tooltip>
        ) : (
          <SoftButton
            color="uproGreen"
            circular
            iconOnly
            disabled={esMozo}
            sx={{
              zIndex: 100,
            }}
            onClick={(event) => {
              event.stopPropagation();
              menu({
                open: event.currentTarget,
                align: "right",
                options: [
                  {
                    name: "Editar",
                    icon: { icon: "edit", color: getUproColor("uproGreen") },
                    onClick: () => {
                      ejecutarAccionUsuario("editar", item);
                    },
                  },
                  {
                    name: "Borrar",
                    icon: { icon: "delete", color: getUproColor("uproPink") },
                    onClick: () => {
                      ejecutarAccionUsuario("borrar", item);
                    },
                  },
                ],
                handleClose: () => { },
              });
            }}
          >
            <Icon>more_vert</Icon>
          </SoftButton>
        )}
      </SoftBox>
    </Card>
  );

  return (
    <SoftBox>
      <Grid
        container
        spacing={2}
        pt={{
          xs: 3,
          xl: 0,
        }}
      >
        <Grid item xs={12} justifyContent="space-between" display="flex" gap={1} flexWrap="wrap">
          <SoftBox>
            <SoftTypography variant="h6">Mesas</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1} flexWrap="wrap">
            <Tooltip
              title={
                modoReordenar
                  ? "Mantenga presionado el handle y arrastre para reordenar"
                  : "Activar reordenamiento manual"
              }
              placement="top"
            >
              <SoftButton
                color={modoReordenar ? "dark" : "info"}
                circular
                onClick={() => setModoReordenar((prev) => !prev)}
                disabled={loadingSaveMesasOrden || Boolean(draggingMesaId)}
              >
                <Icon>{modoReordenar ? "done" : "drag_indicator"}</Icon>
                &nbsp;{modoReordenar ? "Listo" : "Reordenar"}
              </SoftButton>
            </Tooltip>
            <Tooltip title="Agregar nueva mesa" placement="top">
              <SoftButton
                color="primary"
                circular
                onClick={() => {
                  setModalMesa(true);
                }}
                disabled={esMozo || modoReordenar || loadingSaveMesasOrden || Boolean(draggingMesaId)}
              >
                <Icon>add</Icon>
                &nbsp;Nueva mesa
              </SoftButton>
            </Tooltip>
          </SoftBox>
        </Grid>
        <Grid xs={12} display="flex" justifyContent="flex-end" item>
          <SoftBox
            sx={{
              width: {
                xs: "100%",
                sm: "auto",
              },
            }}
          >
            {modoReordenar && (
              <SoftTypography variant="button" color="text">
                Arrastre cada mesa desde el handle lateral. El nuevo orden se guarda al soltar.
              </SoftTypography>
            )}
          </SoftBox>
        </Grid>
      </Grid>
      <SoftBox sx={{ position: "relative" }}>
        {loading || loadingDeleteMesa ? (
          <Loading />
        ) : error ? (
          <CartelError />
        ) : mesas?.length > 0 ? (
          <SoftBox display="flex" flexDirection="column" gap={2}>
            {mesas.map((item) => {
              const dragging = Number(draggingMesaId) === Number(item.id);

              return (
                <SoftBox
                  key={item.id}
                  data-mesa-id={item.id}
                  ref={(node) => {
                    if (node) {
                      itemRefs.current.set(item.id, node);
                    } else {
                      itemRefs.current.delete(item.id);
                    }
                  }}
                  sx={{
                    cursor: modoReordenar ? "default" : "pointer",
                    zIndex: dragging ? 1 : 99,
                    touchAction: modoReordenar ? "none" : "auto",
                  }}
                  onClick={() => {
                    ejecutarAccionUsuario("elegirMesa", item);
                  }}
                >
                  {dragging ? (
                    <SoftBox
                      sx={{
                        height: dragOverlay?.height || 96,
                        borderRadius: "1rem",
                        border: `2px dashed ${getUproColor("uproBlue")}88`,
                        bgcolor: `${getUproColor("uproBlue")}11`,
                      }}
                    />
                  ) : (
                    renderMesaCard(item)
                  )}
                </SoftBox>
              );
            })}
          </SoftBox>
        ) : (
          <SinDatos />
        )}

        {mesaArrastrada && dragOverlay && (
          <SoftBox
            sx={{
              position: "fixed",
              top: dragOverlay.top,
              left: dragOverlay.left,
              width: dragOverlay.width,
              zIndex: 1600,
              pointerEvents: "none",
            }}
          >
            {renderMesaCard(mesaArrastrada, true)}
          </SoftBox>
        )}
      </SoftBox>
      <ModalMesa
        open={modalMesa}
        handleClose={() => {
          setModalMesa(false);
          setSelectedMesa(null);
        }}
        refetch={refetch}
        selectedMesa={selectedMesa}
      />
      <ModalPedido
        open={modalPedido}
        handleClose={() => {
          setModalPedido(false);
          setMesaElegida(null);
        }}
        refetch={refetch}
        mesaElegida={mesaElegida}
        savePedido={savePedido}
        loadingSave={loadingSave}
        stockRefreshToken={stockRefreshTick}
      />
    </SoftBox>
  );
}

export default Mesas;
